import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { ApiStatusPayload } from '~/Interfaces';
import { startAppListening } from '../middlewares/listener.middleware';
import {
  ICardOfferApplicant,
  ICardOfferForm,
  ICardOfferOverview,
  ICardOfferState,
} from '~/interfaces/cardOffer';
import {
  IBeneficialOwner,
  IKYCQuestionnaireAnswer,
  ISigner,
} from '~/interfaces/shared';
import { KycQuestionnaireQuestionNames } from '~/enums';
import { syncOfferPersons } from '~/helpers/shared.helper';
import { ApiStatus } from '../types/sharedTypes';

export const cardOfferInitialState = Object.freeze<ICardOfferState>({
  currentStep: 1,
  stepStartTimestamp: new Date().toString(),
  form: {
    clientId: 0,
    owners: [],
    signers: [],
    personalNumber: '',
    ownershipFiles: [],
    kycQuestionnaireQuestions: [],
    kycQuestionnaireAnswers: [],
    applicationReasons: [],
    applicant: {
      firstName: '',
      lastName: '',
      fullName: '',
      id: '',
      signerUuid: '',
      email: '',
      percentage: '',
      phoneNumber: '',
      signerId: 0,
    },
  },
  overview: {
    applicationUuid: '',
    offeredCreditLimit: 0,
    companyName: '',
    clientId: 0,
    companyType: '',
    additionalSigners: [],
    beneficialOwners: [],
    applicationReasons: [],
    hasAcceptedTerms: false,
    applicant: {
      firstName: '',
      lastName: '',
      fullName: '',
      id: '',
      signerUuid: '',
      email: '',
      percentage: '',
      phoneNumber: '',
      signerId: 0,
    },
  },
  apiStatus: {
    offer: ApiStatus.Idle,
    post: ApiStatus.Idle,
    fetchKycQuestions: ApiStatus.Idle,
    postKycAnswers: ApiStatus.Idle,
  },
});

type IUpdatableFormFields = Omit<ICardOfferForm, 'owners'>;

const cardOfferSlice = createSlice({
  name: 'cardOffer',
  initialState: cardOfferInitialState,
  reducers: {
    goToNextStep: (state) => {
      state.currentStep += 1;
    },
    goToPreviousStep: (state) => {
      state.currentStep -= 1;
    },
    updateForm: (
      state,
      action: PayloadAction<Partial<IUpdatableFormFields>>
    ) => {
      state.form = { ...state.form, ...action.payload };
    },
    updateOverview: (state, action: PayloadAction<ICardOfferOverview>) => {
      state.overview = { ...state.overview, ...action.payload };
    },
    setApiStatus: (state, action: PayloadAction<ApiStatusPayload>) => {
      state.apiStatus = {
        ...state.apiStatus,
        ...action.payload,
      };
    },
    setStepStartTimestamp: (state) => {
      state.stepStartTimestamp = new Date().toString();
    },
    updateSigner: (state, action: PayloadAction<ISigner>) => {
      const { signers } = state.form;

      const updatedList = signers.map((s) => {
        if (s.signerId === action.payload.signerId) {
          return {
            ...s,
            ...action.payload,
          };
        }
        return s;
      });
      state.form.signers = updatedList;
    },
    updateAdditionalOwner: (state, action: PayloadAction<IBeneficialOwner>) => {
      const updatedOwners = state.form.owners.map((o) => {
        if (o.id === action.payload.id) {
          return action.payload;
        }
        return o;
      });

      state.form.owners = updatedOwners;
    },
    addAdditionalOwner: (state, action: PayloadAction<IBeneficialOwner>) => {
      state.form.owners.push(action.payload);
    },
    setBeneficialOwners: (state, action: PayloadAction<IBeneficialOwner[]>) => {
      state.form.owners = action.payload;
    },
    removeAdditionalOwnerById: (state, action: PayloadAction<string>) => {
      state.form.owners = state.form.owners.filter(
        (o) => o.id !== action.payload
      );
    },
    updateKYCAnswers: (
      state,
      action: PayloadAction<IKYCQuestionnaireAnswer>
    ) => {
      const { kycQuestionnaireAnswers, kycQuestionnaireQuestions } = state.form;

      let updatedKYCAnswers = [...kycQuestionnaireAnswers];

      const questionHasAlreadyBeenAnswered = !!kycQuestionnaireAnswers.find(
        (item) => item.questionId === action.payload.questionId
      );
      if (questionHasAlreadyBeenAnswered) {
        updatedKYCAnswers = kycQuestionnaireAnswers.map((item) => {
          if (item.questionId === action.payload.questionId) {
            return action.payload;
          }
          return item;
        });
      } else {
        updatedKYCAnswers.push(action.payload);
      }

      const handleCashQuestionId = kycQuestionnaireQuestions.find(
        (question) =>
          question.name === KycQuestionnaireQuestionNames.handleCashQuestion ||
          question.name === KycQuestionnaireQuestionNames.handleCashQuestionNew
      )?.id;

      // to remove the answer to the question "How much cash do you handle?" in case user answered
      // "NO" to the question "Do you handle cash within your organization?"
      if (
        action.payload.questionId === handleCashQuestionId &&
        action.payload.answers[0].answer === 'NO'
      ) {
        const handleCashToWhatExtentQuestionId = kycQuestionnaireQuestions.find(
          (question) =>
            question.name ===
              KycQuestionnaireQuestionNames.handleCashToWhatExtentQuestion ||
            question.name ===
              KycQuestionnaireQuestionNames.handleCashPercentageTurnoverQuestion
        )?.id;
        updatedKYCAnswers = updatedKYCAnswers.filter(
          (item) => item.questionId !== handleCashToWhatExtentQuestionId
        );
      }

      state.form.kycQuestionnaireAnswers = updatedKYCAnswers;
    },
    updateApplicant: (
      state,
      action: PayloadAction<Partial<ICardOfferApplicant>>
    ) => {
      state.form.applicant = {
        ...state.form.applicant,
        ...action.payload,
      };
    },
  },
});

export const {
  goToNextStep,
  goToPreviousStep,
  updateForm,
  updateOverview,
  setApiStatus,
  updateSigner,
  addAdditionalOwner,
  updateAdditionalOwner,
  removeAdditionalOwnerById,
  updateKYCAnswers,
  updateApplicant,
  setBeneficialOwners,
} = cardOfferSlice.actions;

export type TCardOfferSliceActions = typeof cardOfferSlice.actions;

export default cardOfferSlice.reducer;

startAppListening({
  matcher: isAnyOf(goToNextStep, goToPreviousStep),
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(cardOfferSlice.actions.setStepStartTimestamp());
  },
});

// if signers, owners or the applicant is updated, we need to check if the same person is in the
// other lists and update it there as well
startAppListening({
  matcher: isAnyOf(
    addAdditionalOwner,
    updateAdditionalOwner,
    updateSigner,
    updateApplicant,
    setBeneficialOwners,
    removeAdditionalOwnerById
  ),
  effect: (action, listenerApi) => {
    syncOfferPersons(
      action,
      listenerApi.getState().cardOffer,
      listenerApi.getOriginalState().cardOffer,
      listenerApi.dispatch,
      cardOfferSlice.actions
    );
  },
});
